import { useState } from 'react';
import { Target, MessageSquare } from 'react-feather';
import styled from '@emotion/styled';
import type { Chat } from '@trinity/chat';
import { segmentationBuilderApi } from './segmentation-api';

interface SegmentationStrategyProps {
  queryId: number;
  queryIdentifier: string;
  value: Chat.QueryState.SegmentationStrategy;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 750px;
  margin: 0 auto;
`;

const ModeToggle = styled.div`
  display: flex;
  gap: 8px;
  background: #f2f2f2;
  padding: 4px;
  border-radius: 10px;
`;

const ModeBtn = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 10px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  background: ${(p) => (p.$active ? '#ffffff' : 'transparent')};
  color: ${(p) => (p.$active ? '#28AAE1' : '#626262')};
  box-shadow: ${(p) => (p.$active ? '0 2px 8px rgba(0,0,0,0.08)' : 'none')};
  &:hover {
    color: ${(p) => (p.$active ? '#28AAE1' : '#323232')};
  }
`;

const StrategyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
`;

const StrategyCard = styled.button<{ $selected: boolean }>`
  display: block;
  width: 100%;
  text-align: left;
  padding: 16px;
  border-radius: 10px;
  border: 2px solid ${(p) => (p.$selected ? '#28AAE1' : '#e5e5e5')};
  background: ${(p) => (p.$selected ? '#E5F5FC' : '#ffffff')};
  color: #323232;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${(p) => (p.$selected ? '0 2px 12px rgba(40,170,225,0.15)' : '0 1px 3px rgba(0,0,0,0.04)')};
  &:hover {
    border-color: #28aae1;
    transform: translateY(-1px);
  }
`;

const StrategyLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #323232;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StrategyDesc = styled.div`
  font-size: 13px;
  color: #626262;
  line-height: 1.5;
`;

const CustomObjectiveCard = styled.div`
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  padding: 16px;
`;

const CustomLabel = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #323232;
  margin-bottom: 8px;
`;

const CustomTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  font-size: 14px;
  color: #323232;
  font-family: inherit;
  resize: vertical;
  outline: none;
  &::placeholder {
    color: #adadad;
  }
  &:focus {
    border-color: #28aae1;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 14px;
  border-radius: 12px;
  border: none;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background: #28aae1;
  color: #ffffff;
  transition: all 0.2s;
  box-shadow: 0 4px 16px rgba(40, 170, 225, 0.3);
  &:hover:not(:disabled) {
    background: #1d94c7;
    transform: translateY(-1px);
  }
  &:disabled {
    background: #e5e5e5;
    color: #adadad;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const STRATEGIES = [
  {
    key: 'defend_share',
    label: 'Defend Existing Strong Share',
    description: 'Identify and protect HCPs with high loyalty and market share',
  },
  {
    key: 'grow_early_extensive',
    label: 'Grow from Early to Extensive Use',
    description: 'Find HCPs with early adoption potential and expand their usage',
  },
  {
    key: 'convert_similar',
    label: 'Convert from Similar Treatment',
    description: 'Target HCPs currently using similar therapy alternatives',
  },
  {
    key: 'educate_complexity',
    label: 'Educate on Complexity',
    description: 'Reach HCPs treating complex patients who need advanced therapy',
  },
  {
    key: 'custom_objective',
    label: 'Custom Objective',
    description: 'Define your own segmentation objective',
  },
];

export function SegmentationStrategy({ queryId, queryIdentifier, value }: SegmentationStrategyProps) {
  const [qualMode, setQualMode] = useState<'data_driven' | 'qual_informed' | 'qual_primary'>(
    value.qualMode || 'data_driven'
  );
  const [selectedStrategy, setSelectedStrategy] = useState<string | null>(value.selectedStrategy);
  const [customObjective, setCustomObjective] = useState(value.customObjective || '');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (): Promise<void> => {
    if (!selectedStrategy) return;

    setSubmitting(true);
    try {
      await segmentationBuilderApi.submitStrategy(queryIdentifier, {
        strategy_key: selectedStrategy === 'custom_objective' ? undefined : selectedStrategy,
        nl_input: selectedStrategy === 'custom_objective' ? customObjective : undefined,
        qual_mode: qualMode,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = selectedStrategy && (selectedStrategy !== 'custom_objective' || customObjective.trim());

  return (
    <Wrapper>
      <ModeToggle>
        <ModeBtn $active={qualMode === 'data_driven'} onClick={() => setQualMode('data_driven')}>
          Data-Driven
        </ModeBtn>
        <ModeBtn $active={qualMode === 'qual_informed'} onClick={() => setQualMode('qual_informed')}>
          Qual-Informed
        </ModeBtn>
        <ModeBtn $active={qualMode === 'qual_primary'} onClick={() => setQualMode('qual_primary')}>
          Qual-Led
        </ModeBtn>
      </ModeToggle>

      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#323232', marginBottom: 12 }}>
          Select Your Segmentation Strategy
        </div>
        <StrategyGrid>
          {STRATEGIES.map((strategy) => (
            <StrategyCard
              key={strategy.key}
              $selected={selectedStrategy === strategy.key}
              onClick={() => setSelectedStrategy(strategy.key)}
            >
              <StrategyLabel>
                {strategy.key === 'custom_objective' ? <MessageSquare size={16} /> : <Target size={16} />}
                {strategy.label}
              </StrategyLabel>
              <StrategyDesc>{strategy.description}</StrategyDesc>
            </StrategyCard>
          ))}
        </StrategyGrid>
      </div>

      {selectedStrategy === 'custom_objective' && (
        <CustomObjectiveCard>
          <CustomLabel>Describe your custom segmentation objective:</CustomLabel>
          <CustomTextarea
            value={customObjective}
            onChange={(e) => setCustomObjective(e.target.value)}
            placeholder="e.g., I want to segment HCPs based on their patient complexity and treatment patterns to identify those who would benefit from targeted education programs..."
          />
        </CustomObjectiveCard>
      )}

      <SubmitButton type="button" disabled={!canSubmit || submitting} onClick={handleSubmit}>
        {submitting ? 'Processing Strategy...' : 'Set Objective & Generate Features'}
      </SubmitButton>
    </Wrapper>
  );
}
